import { Container, Texture, Ticker } from "pixi.js";
import { Emitter, EmitterConfigV3 } from "@pixi/particle-emitter";
import { PromiseCompletionSource } from "../../../../../shared/async/PromiseCompletionSource";

export class CoinsExplowAnimationView extends Container {
    public static readonly BLOW_EMIT_DURATION: number = 100/*ms*/;

    private readonly vcs: PromiseCompletionSource<void>;
    private readonly emitter: Emitter;
    private readonly particlesLifetimeMs: number;

    public isActive: boolean;
    private animationTimeMs: number;

    constructor(textures: Array<Texture>) {
        super();
        const config = this.setupAnimation(textures);
        this.emitter = new Emitter(this, config);
        this.particlesLifetimeMs = config.lifetime.max * 1000;
        this.animationTimeMs = 0;
        this.vcs = new PromiseCompletionSource();
    }

    private setupAnimation(textures: Array<Texture>): EmitterConfigV3 {
        return {
            "lifetime": {
                "min": 2,
                "max": 2
            },
            "frequency": 0.05,
            "particlesPerWave": 10,
            "emitterLifetime": 0.25,
            "maxParticles": 1000,
            "addAtBack": false,
            "pos": {
                "x": 0,
                "y": 0
            },
            "behaviors": [
                {
                    "type": "moveSpeed",
                    "config": {
                        "speed": {
                            "list": [
                                {
                                    "time": 0,
                                    "value": 1950
                                },
                                {
                                    "time": 0.05,
                                    "value": 1050
                                },
                                {
                                    "time": 0.9,
                                    "value": 1500
                                },
                                {
                                    "time": 1,
                                    "value": 3000
                                }
                            ]
                        },
                        "minMult": 0.6
                    }
                },
                {
                    "type": "alpha",
                    "config": {
                        "alpha": {
                            "list": [
                                {
                                    "time": 0.6,
                                    "value": 1

                                },
                                {
                                    "time": 0.8,
                                    "value": 0.5,
                                },
                                {
                                    "time": 1,
                                    "value": 0
                                }
                            ]
                        }
                    }
                },
                {
                    "type": "spawnShape",
                    "config": {
                        "type": "torus",
                        "data": {
                            "x": 0,
                            "y": 0,
                            "radius": 100,
                            "innerRadius": 80,
                            "affectRotation": false
                        }
                    }
                },
                {
                    "type": "scaleStatic",
                    "config": {
                        "min": 1,
                        "max": 3
                    }
                },
                {
                    "type": "scale",
                    "config": {
                        "scale": {
                            "list": [
                                {
                                    "value": 0.9,
                                    "time": 0
                                },
                                {
                                    "value": 1.0,
                                    "time": 1
                                }],
                            "isStepped": true
                        },
                        "minMult": 1.0
                    }
                },
                {
                    "type": "moveAcceleration",
                    "config": {
                        "accel": {
                            "x": 0,
                            "y": 2000
                        },
                        "minStart": 2000,
                        "maxStart": 3000,
                        "rotate": true
                    }
                },
                {
                    "type": "rotationStatic",
                    "config": {
                        "min": -150,
                        "max": -30
                    }
                },
                {
                    "type": "animatedSingle",
                    "config": {
                        "anim": {
                            "loop": true,
                            "textures": textures
                        }
                    }
                }
            ]
        }
    }

    public async play(): Promise<void> {
        this.emitter.autoUpdate = true;
        Ticker.shared.add(this.updateTime, this);
        this.isActive = true;
        await this.vcs.result;
    }

    public async continue(): Promise<void> {
        this.emitter.autoUpdate = true;
        Ticker.shared.add(this.updateTime, this);
        await this.vcs.result;
    }

    public stop(): void {
        Ticker.shared.remove(this.updateTime, this);
        this.emitter.autoUpdate = false;
    }

    public setProgress(progress: number): void {
        this.animationTimeMs = this.particlesLifetimeMs * progress;
    }

    private updateTime(): void {
        this.animationTimeMs += Ticker.shared.elapsedMS * 2;
        if (this.animationTimeMs >= this.particlesLifetimeMs) {
            this.emitter.autoUpdate = false;
            Ticker.shared.remove(this.updateTime, this);
            this.isActive = false;
            this.vcs.complete();
        }
    }
}